"use client";

import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";

type HeroBackgroundGridProps = {
  cellSize?: number;
  className?: string;
  /**
   * When true, rows/cols follow the container so the grid covers the hero.
   */
  fillContainer?: boolean;
  rows?: number;
  cols?: number;
};

/**
 * Decorative grid only (no interaction, no ripple). Fills the measured area.
 */
export function HeroBackgroundGrid({
  cellSize = 44,
  className,
  fillContainer = true,
  rows: rowsProp = 12,
  cols: colsProp = 24,
}: HeroBackgroundGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [{ w, h }, setSize] = useState({ w: 0, h: 0 });

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const measure = () => {
      const r = el.getBoundingClientRect();
      setSize({ w: r.width, h: r.height });
    };

    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { rows, cols } = useMemo(() => {
    if (!fillContainer) {
      return { rows: rowsProp, cols: colsProp };
    }
    const effW = w > 0 ? w : 1280;
    const effH = h > 0 ? h : 720;
    const c = Math.max(12, Math.ceil(effW / cellSize) + 2);
    const r = Math.max(10, Math.ceil(effH / cellSize) + 2);
    return { rows: r, cols: c };
  }, [fillContainer, rowsProp, colsProp, w, h, cellSize]);

  const cells = useMemo(
    () => Array.from({ length: rows * cols }, (_, idx) => idx),
    [rows, cols],
  );

  const gridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`,
    gridTemplateRows: `repeat(${rows}, ${cellSize}px)`,
    width: cols * cellSize,
    height: rows * cellSize,
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        "absolute inset-0 overflow-hidden",
        "[--cell-border-color:color-mix(in_srgb,color-mix(in_srgb,var(--design-ambient-tint)_42%,var(--primary))_32%,transparent)]",
        "[--cell-fill:color-mix(in_srgb,color-mix(in_srgb,var(--primary)_22%,var(--design-ambient-tint))_6%,transparent)]",
        className,
      )}
      aria-hidden
    >
      <div className="hero-grid-mask absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute left-1/2 top-1/2 z-[1] -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          style={gridStyle}
        >
          {cells.map((idx) => (
            <div
              key={idx}
              className="relative border-[0.5px] opacity-[0.3] pointer-events-none"
              style={{
                backgroundColor: "var(--cell-fill)",
                borderColor: "var(--cell-border-color)",
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
